import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

// Spinner shown while waiting for the first answer chunk.
//
// Deliberately boring: a stock MUI CircularProgress. The previous
// bouncing-dots design depended on custom @keyframes + per-dot opacity
// animation and repeatedly shipped invisible (opacity troughs, keyframe
// scoping). CircularProgress has no opacity-0 state, no custom keyframes,
// and is fully visible from the first painted frame.
//
// `showStatus` adds a line of text for Ask Prompta's agent: the BE sends a
// `status` frame ({ tool, label }) as each lookup starts, and `label` is shown
// as-is (already in the report's language). Between lookups — while the model
// is thinking — `status` is null or has no label, and the line cycles through
// THINKING_WORDS instead.
export const THINKING_WORDS = [
    { id: 'askPrompta.thinking.sleuthing', defaultMessage: 'Sleuthing' },
    { id: 'askPrompta.thinking.synthesizing', defaultMessage: 'Synthesizing' },
    { id: 'askPrompta.thinking.connecting', defaultMessage: 'Connecting the dots' },
    { id: 'askPrompta.thinking.mesmerizing', defaultMessage: 'Mesmerizing' },
    { id: 'askPrompta.thinking.reading', defaultMessage: 'Reading between the lines' },
    { id: 'askPrompta.thinking.untangling', defaultMessage: 'Untangling the threads' },
    { id: 'askPrompta.thinking.weighing', defaultMessage: 'Weighing the evidence' },
];
export const THINKING_INTERVAL_MS = 2400;

const ThinkingLine = ({ status }) => {
    const intl = useIntl();
    const [index, setIndex] = useState(0);
    const label = status?.label;

    useEffect(() => {
        if (label) return undefined;
        const timer = setInterval(
            () => setIndex(i => (i + 1) % THINKING_WORDS.length),
            THINKING_INTERVAL_MS,
        );
        return () => clearInterval(timer);
    }, [label]);

    const text = label || `${intl.formatMessage(THINKING_WORDS[index])}…`;
    return (
        <Typography
            variant="body2"
            aria-live="polite"
            sx={{ color: 'text.secondary', fontSize: 13, ml: 1 }}
        >
            {text}
        </Typography>
    );
};

const TypingIndicator = ({ showStatus = false, status = null }) => (
    <Box
        aria-label="…"
        sx={{ display: 'flex', alignItems: 'center', px: 0.5, py: 0.25 }}
    >
        <CircularProgress size={16} thickness={5} disableShrink />
        {showStatus && <ThinkingLine status={status} />}
    </Box>
);

export default TypingIndicator;
